import { filterListByCurrentIndustryCode } from "../../Utils";
import { Footer } from "../../components/Footer";
import { Layout } from "../../components/Layout";
import { RelatedCaseStudies } from "../../components/RelatedCaseStudies";
import { graphql } from "gatsby";
import * as React from "react";
import { Helmet } from "react-helmet";

export default function CaseStudy({ data, location }) {
  return (
    <Layout location={location}>
      <main id="main" className="marketing-content container-fluid">
        <Helmet
          bodyAttributes={{
            class: "body-shorthand body-marketing body-marketing-secondary body-marketing-case-studies hide-elevio",
          }}
        >
          <title>Seven West Media | Shorthand Case Study</title>
          <meta
            name="description"
            content="How Seven West Media's sponsored content studio uses Shorthand to delight clients and lock in repeat business."
          />
        </Helmet>
        <div className="row section-title">
          <div className="col-sm-12 col-md-10 col-lg-8 col-xl-6  mx-auto  section-inner">
            <h1>
              <span className="shorthand-logo">Shorthand</span>
              <span className="subsection-label">Case Study:</span>
              Seven West Media
            </h1>
            <h2>
              How Seven West Media&apos;s sponsored content studio uses Shorthand to delight clients and lock in repeat business.
            </h2>
          </div>
          <div aria-hidden="true" className="background-media">
            <div className="tw-absolute tw-inset-0 tw-z-10 tw-bg-black tw-bg-opacity-65"></div>
            <img
              src="/media/case-studies/SevenWestMedia_CaseStudy_Title_IMG.png"
              data-object-fit="cover"
              data-autoscale-content-ratio="1.77777777778"
              alt=""
            />
          </div>
        </div>
        <div className="row section">
          <div className="col-sm-12 col-md-10 col-lg-8 col-xl-6  mx-auto marketing-main-column">
            <h3 className="intro">
              Using Shorthand since 2019, MINT creates scroll-stopping sponsored content for <i>PerthNow</i> and{" "}
              <i>The West Australian</i>, delivering consistently high page views for advertisers.
            </h3>
            <br />
            <div className="inline-image featured ">
              <img src="/media/case-studies/SevenWestMedia_CaseStudy_Hero.gif" alt="Seven west media" data-object-fit="cover" />
            </div>
            <p>
              Seven West&apos;s commercial content studio, MINT, builds immersive sponsor stories quickly and simply, in a way that
              delights readers, delivers traffic, and keeps client brands coming back for more.
            </p>
            <p>Todd Pascoe is the graphic designer for the team and Jacinta Houzer is the creative operations manager.</p>
            <p>
              While they offer a range of sponsored content products, it&apos;s Shorthand that they use to build bespoke features and
              immersive stories.
            </p>
            <p>&quot;We like to apply it where we can,&quot; says Pascoe.</p>
            <p>&quot;Any sort of visually-led webpage that we need designed, we&apos;ll use Shorthand,&quot; says Houzer. </p>
            <p>
              The MINT team builds two types of partner content with Shorthand. The first is called an immersive. These are designed
              especially for the client&apos;s campaign, with all the creative elements of the brand and its message, under the
              relevant Seven West masthead. These often include multiple stories as a series, built using Shorthand&apos;s Projects
              offering.
            </p>
            <figure className="block quote-block offset right">
              <blockquote>
                <p>&quot;Showing previous work we&apos;ve done on Shorthand is quite a big selling point for our business.&quot;</p>
              </blockquote>
              <figcaption className="quote-cite">Todd Pascoe</figcaption>
            </figure>
            <p>
              One example is &apos;
              <a href="https://biteclub.perthnow.com.au/" target="_blank">
                Perth&apos;s Bite Club
              </a>
              &apos;, a hub of articles showcasing venues and cuisine around the city.
            </p>
            <p>The second is called a feature article, which is normally one page with imagery and text.</p>
            <p>
              The editorial team at Seven West also has access to Shorthand, and they&apos;re &quot;looking to expand their
              capabilities&quot;, says Houzer. &quot;So there are use cases across the business.&quot;
            </p>
            <h3 className="subheading">Delivering results</h3>
            <p>MINT&apos;s immersives are so successful and reliable that Houzer says &quot;The product speaks for itself&quot;.</p>
            <p>
              &quot;For each immersive we guarantee a minimum of 20,000 page views and then for feature articles it&apos;s 10,000 page
              views,&quot; she says. &quot;Depending on the content, they [can] really smash it out of the park, so sometimes we get
              more than that.&quot;
            </p>
            <p>
              These strong results help with winning repeat business from clients. &quot;They&apos;re happy with the outcomes; they
              come back.&quot;
            </p>
            <figure className="block quote-block offset left">
              <blockquote>
                <p>
                  &quot;For each immersive we have minimum guarantees for page views, so it&apos;s great when we exceed these and are
                  able to report back to the client with over delivery.&quot;
                </p>
              </blockquote>
              <figcaption className="quote-cite">Jacinta Houzer</figcaption>
            </figure>
            <p>
              Pascoe reflects on one particular immersive series produced for a WA mining company that was celebrating a milestone
              birthday.
            </p>

            <p>
              &quot;We put a lot of effort using Shorthand to produce a celebratory history hub about them. We are unbelievably happy
              with the look and feel of the final result.
            </p>
            <p>&quot;It definitely increased our positive relationship with that company,&quot; he said.</p>
            <p>&quot;It really had quite a positive impact, with that relationship, with our advertising department.&quot;</p>
            <p>
              &quot;The process was so smooth. There was no stress in the design side of things, there was no stress with the client.
              We hit design deadlines early, which I&apos;m feeling had a lot to do with Shorthand&apos;s capabilities, and how quickly
              we could execute the final design.&quot;
            </p>

            <div className="photo-grid flex featured">
              <img
                className="col-12 col-sm-6"
                src="/media/case-studies/SevenWestMedia_CaseStudy_40ThingsSummer_1.png"
                alt="perth now"
              />
              <img
                className="col-12 col-sm-6"
                src="/media/case-studies/SevenWestMedia_CaseStudy_40ThingsSummer_2.png"
                alt="perth now"
              />
              <img
                className="col-12 col-sm-6"
                src="/media/case-studies/SevenWestMedia_CaseStudy_40ThingsSummer_3.png"
                alt="perth now"
              />
              <img
                className="col-12 col-sm-6"
                src="/media/case-studies/SevenWestMedia_CaseStudy_40ThingsSummer_4.png"
                alt="perth now"
              />
            </div>
            <div className="caption">&apos;40 things to do in Perth this summer&apos; series.</div>
            <h3 className="subheading">Repeated success</h3>
            <p>
              The MINT team&apos;s fantastic designs and approach to partner branding have become a hit with clients and readers — and
              a strong source of repeat business.
            </p>
            <p>
              Members of the sales team send prospective clients links to previous work to give them an idea of what it can look like.
              &quot;Showing examples of what we&apos;ve done on Shorthand is quite a big selling point,&quot; says Pascoe.{" "}
            </p>
            <p>
              &quot;What we produce on Shorthand is one of our higher tier products… One way we get clients on board is showing them
              the visuals.&quot;
            </p>
            <p>
              Once a client has booked a story or a series, Pascoe designs a &apos;custom skin&apos;. &quot;Essentially a mini brand
              guide for that piece of content. We&apos;ll give them the visual direction, the colours, the fonts, we&apos;ll do a short
              mock-up of what the immersive would look like if we produced it, to give the client an idea of visual direction.&quot;
            </p>
            <p>And it works.</p>
            <p>&quot;Usually that mock ends up being very close to the final build,&quot; Pascoe says.</p>
            <p>
              Houzer says the evergreen nature of the team&apos;s immersive content is an attractive point for clients. &quot;It&apos;s
              not like a social media post that you might only see for a short amount of time; that website will exist forever.&quot;
            </p>
            <h3 className="subheading">Ease and speed</h3>
            <p>With heavy workloads, media and creative teams need tools that can keep them moving fast.</p>
            <p>Shorthand, says Houzer, is &quot;Very easy to use and intuitive. We can load the brand&apos;s elements in.&quot;</p>
            <p>
              &quot;I think I picked it up in a day when I first started working with it,&quot; says Pascoe. &quot;The interface is not
              hard to pick up. We use minimal coding here and there.&quot;
            </p>
            <h3 className="subheading">Reliability and service</h3>
            <p>Shorthand&apos;s reliability means MINT is able to prepare mockups and final designs simply and with confidence.</p>
            <p>
              &quot;Producing an immersive with Shorthand hardly ever goes off the rails,&quot; says Pascoe. &quot;Design elements
              generally don&apos;t shift or change; it&apos;s not as unexpected as, say, other products, which can have a lot of
              factors involved.&quot;
            </p>
            <p>
              And, in the odd case where the team might need a bit of help with a visual or layout issue, the Shorthand team is happy
              to jump in. &quot;It&apos;s always been fixed in a timely manner, so that&apos;s good,&quot; says Pascoe.
            </p>
            <figure className="block quote-block offset right">
              <blockquote>
                <p>&quot;What we produce on Shorthand we consider a higher tier product.&quot;</p>
              </blockquote>
              <figcaption className="quote-cite">Todd Pascoe</figcaption>
            </figure>
            <p>
              With client feedback and changes a necessary part of the production process for any piece of great partner media,
              it&apos;s handy to have a product that can be changed and iterated cleanly.
            </p>
            <p>&quot;Shorthand&apos;s pretty airtight compared to a lot of other programs that I&apos;ve used&quot;, Pascoe says.</p>
            <p>
              &quot;You don&apos;t have to keep this eagle eye on whether things are going to look the same the next day, or if
              you&apos;ve changed a bit of code and things have shifted out of place.&quot;
            </p>
            <p>&quot;It&apos;s very sophisticated in that way.&quot;</p>

            <h3 className="subheading">Perfecting the process</h3>
            <p>
              As he gets even more confident with the platform, Pascoe has been implementing his own coding. &quot;I really like the
              option to put your own HTML into the design,&quot; he says.
            </p>
            <p>
              &quot;I like to shift things up two nudges, or tiny bits, I use code for that really, or changing line heights, or
              changing text.&quot;
            </p>
            <p>
              He&apos;s also getting faster and learning the beauty of restraint. &quot;When I first started using it I thought &apos;I
              want to put all these effects and animations and features into one project&apos;. Over time, I&apos;ve gone &apos;No,
              just let the design breathe a bit. Maybe one or two&apos;. It&apos;s become second nature because we use it a lot.&quot;
            </p>
            <p>
              One favourite Pascoe mentions in our chat is &apos;
              <a href="https://summer.perthnow.com.au/2023/" target="_blank">
                40 things to do in Perth this summer
              </a>
              &apos;, designed for Destination Perth, the city&apos;s department of tourism. Perth, the city&apos;s department of
              tourism.
            </p>
            <div className="caption">A selection of sections from MINT&apos;s immersive stories</div>
            <div className="photo-grid flex featured">
              <img className="col-12 col-sm-6" src="/media/case-studies/SevenWestMedia_CaseStudy_Selection_1.png" alt="perth now" />
              <img className="col-12 col-sm-6" src="/media/case-studies/SevenWestMedia_CaseStudy_Selection_2.png" alt="perth now" />
              <img className="col-12 col-sm-6" src="/media/case-studies/SevenWestMedia_CaseStudy_Selection_3.png" alt="perth now" />
              <img className="col-12 col-sm-6" src="/media/case-studies/SevenWestMedia_CaseStudy_Selection_4.png" alt="perth now" />
            </div>
            <h3 className="subheading">Tips for other publishers</h3>
            <p>What are the MINT team&apos;s tips for other Shorthand users?</p>
            <p>
              &quot;Trial and error,&quot; says Todd. And Shorthand comes in handy here too. &quot;You can try something, if it
              doesn&apos;t look good, you can take a step back, you can add something else in.&quot;
            </p>
            <p>
              &quot;I always have two windows open, the backend where you build it, and the preview. I&apos;ll just continually refresh
              both, while I build.&quot;
            </p>
            {/* <figure className="block quote-block offset left">
              <blockquote>
                <p>&quot;It&apos;s nice to use for the pitch phase so clients can actually see how the page will flow.&quot;</p>
              </blockquote>
              <figcaption className="quote-cite">Sara Jeong</figcaption>
            </figure> */}
          </div>
        </div>
        <RelatedCaseStudies studies={filterListByCurrentIndustryCode(data, "tripadvisor")} />
        <Footer />
      </main>
    </Layout>
  );
}

export const query = graphql`
  query RelxQuery {
    allCaseStudiesJson {
      edges {
        node {
          jsonId
          image
          industry
          industryCode
          organisation
          summary
        }
      }
    }
  }
`;
